// pages/master-data/dashboard/DashboardFuelPage.jsx
'use client';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Divider,
  Chip,
  IconButton,
  Autocomplete,
  Checkbox,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DashboardIcon from '@mui/icons-material/Dashboard';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import DataTableV2 from '../../../components/DataTableV2';

import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { useLanguage } from '../../../utils/i18n';

import { useMasterVehicleList } from '../../../stores/masterVehicleStore';
import { fleetApi } from '../../../api/axiosClient';
import { fetchRetase } from '../../../stores/retaseStore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { timezone } from '../../../utils/timeZone';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

import { selectFieldStyle } from './constant';

const formatDateMinusTz = (dateStr, timeStr, tz = 7) => {
  const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr;
  const timePart = dateStr.includes(' ') ? dateStr.split(' ')[1] : timeStr;
  return dayjs(`${datePart} ${timePart}`).subtract(tz, 'hour').format('YYYY-MM-DD[T]HH:mm:ss[Z]');
};

const getFuelPriceLines = (dataPoint) => {
  const vehiclePrices = dataPoint?.vehicle_fuel_prices || [];
  if (!vehiclePrices || vehiclePrices.length === 0) {
    return null;
  }

  const hasAssigned = vehiclePrices.some((vp) => !vp.isDefault);
  if (!hasAssigned) {
    return { title: 'Default', price: 'Rp. 6.800', hasAssigned };
  }

  const assignedList = vehiclePrices.filter((vp) => !vp.isDefault);
  const uniquePrices = [...new Set(assignedList.map((vp) => vp.price))];

  if (uniquePrices.length === 1) {
    const primary = assignedList[0];
    const nameStr = primary.fuel_type_name || primary.licensePlate || 'Solar';
    return { title: nameStr, price: `Rp. ${Number(primary.price).toLocaleString('id-ID')}`, hasAssigned };
  } else {
    const minP = Math.min(...uniquePrices);
    const maxP = Math.max(...uniquePrices);
    return {
      title: 'Multiple Prices',
      price: `Rp. ${Number(minP).toLocaleString('id-ID')} - ${Number(maxP).toLocaleString('id-ID')}`,
      hasAssigned
    };
  }
};

const CustomFuelPriceDot = (props) => {
  const { cx, cy, payload, index, chartData } = props;
  if (!cx || !cy) return null;

  const currentLines = getFuelPriceLines(payload);
  if (!currentLines) {
    return <circle cx={cx} cy={cy} r={4} fill="#10B981" stroke="#ffffff" strokeWidth={2} />;
  }

  let showFlag = false;
  if (index === 0) {
    showFlag = true;
  } else if (chartData) {
    const prevLines = getFuelPriceLines(chartData[index - 1]);
    if (!prevLines || prevLines.price !== currentLines.price || prevLines.title !== currentLines.title) {
      showFlag = true;
    }
  } else {
    // fallback if chartData not passed
    showFlag = true;
  }

  const { title, price, hasAssigned } = currentLines;

  if (!showFlag) {
    return <circle cx={cx} cy={cy} r={4} fill="#10B981" stroke="#ffffff" strokeWidth={2} />;
  }

  const titleLength = title.length;
  const priceLength = price.length;
  const maxLen = Math.max(titleLength, priceLength);
  const badgeWidth = Math.max(90, maxLen * 6.5 + 16);
  const badgeHeight = 36;
  const badgeStartX = -badgeWidth / 2;

  return (
    <g key={`fuel-flag-${payload.fullDate}-${cx}`}>
      <circle cx={cx} cy={cy} r={6} fill={hasAssigned ? '#485D9A' : '#F59E0B'} stroke="#ffffff" strokeWidth={2} />
      <g transform={`translate(${cx}, ${cy - 18})`}>
        <rect
          x={badgeStartX}
          y="-30"
          width={badgeWidth}
          height={badgeHeight}
          rx="4"
          fill="#1E293B"
          stroke={hasAssigned ? '#485D9A' : '#F59E0B'}
          strokeWidth="1"
          style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))' }}
        />
        <polygon points="0,12 -5,6 5,6" fill="#1E293B" />
        <text fill={hasAssigned ? '#38BDF8' : '#FCD34D'} fontSize="10" fontWeight="bold" textAnchor="start">
          <tspan x={badgeStartX + 8} y="-16">
            {title}
          </tspan>
          <tspan x={badgeStartX + 8} y="-2">
            {price}
          </tspan>
        </text>
      </g>
    </g>
  );
};

const CustomChartTooltip = ({ active, payload, label, labelUnit, t }) => {
  const [tooltipPage, setTooltipPage] = useState(0);

  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  const actualFuel = dataPoint.actual_fuel_consumption ?? 0;
  const standardFuel = dataPoint.standard_fuel_consumption ?? 0;
  const actualCost = dataPoint.actual_cost ?? 0;
  const standardCost = dataPoint.standard_cost ?? 0;

  const vehiclePrices = dataPoint.vehicle_fuel_prices || [];
  const pageSize = 5;
  const totalPages = Math.ceil(vehiclePrices.length / pageSize) || 1;

  const currentPage = tooltipPage >= totalPages ? 0 : tooltipPage;
  const paginatedPrices = vehiclePrices.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <Paper
      elevation={8}
      sx={{
        p: 1.5,
        bgcolor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(8px)',
        border: '1px solid #CBD5E1',
        borderRadius: 2,
        width: 275,
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
        pointerEvents: 'auto'
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.primary"
        sx={{ borderBottom: '1px solid #E2E8F0', pb: 0.5, mb: 1, display: 'block' }}
      >
        📅 {dataPoint.fullDate ? dayjs(dataPoint.fullDate).format('DD MMMM YYYY') : label}
      </Typography>

      {/* COMPACT FUEL & COST SUMMARY */}
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        {/* Actual Fuel */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Actual Fuel:
            </Typography>
          </Box>
          <Typography variant="caption" fontWeight={700} color="text.primary">
            {Number(actualFuel).toLocaleString('id-ID')} {labelUnit}
          </Typography>
        </Box>

        {/* Actual Cost */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', pl: 1.75 }}>
          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
            Actual Cost:
          </Typography>
          <Typography variant="caption" fontWeight={700} color="#10B981" fontSize="0.75rem">
            Rp {Number(actualCost).toLocaleString('id-ID')}
          </Typography>
        </Box>

        {/* Standard Fuel */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', pt: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3B82F6' }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Standard Fuel:
            </Typography>
          </Box>
          <Typography variant="caption" fontWeight={700} color="text.primary">
            {Number(standardFuel).toLocaleString('id-ID')} {labelUnit}
          </Typography>
        </Box>

        {/* Standard Cost */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', pl: 1.75 }}>
          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
            Standard Cost:
          </Typography>
          <Typography variant="caption" fontWeight={700} color="#3B82F6" fontSize="0.75rem">
            Rp {Number(standardCost).toLocaleString('id-ID')}
          </Typography>
        </Box>
      </Stack>

      {/* MINIMALIST FUEL PRICE LIST WITH PAGINATION PER 5 DATA */}
      <Box sx={{ pt: 1, borderTop: '1px dashed #CBD5E1' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="primary.main" fontSize="0.75rem">
            🏷️ {t ? t('dashboardFuel.fuelPrice', 'Fuel Price') : 'Fuel Price'} ({vehiclePrices.length})
          </Typography>
          {totalPages > 1 && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                disabled={currentPage === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setTooltipPage((p) => Math.max(0, p - 1));
                }}
                sx={{ p: 0.2, border: '1px solid #CBD5E1', bgcolor: '#F8FAFC' }}
              >
                <ArrowBackIosIcon sx={{ fontSize: 10 }} />
              </IconButton>
              <Typography variant="caption" color="text.secondary" fontSize="0.65rem" fontWeight={700}>
                {currentPage + 1}/{totalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={currentPage >= totalPages - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  setTooltipPage((p) => Math.min(totalPages - 1, p + 1));
                }}
                sx={{ p: 0.2, border: '1px solid #CBD5E1', bgcolor: '#F8FAFC' }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 10 }} />
              </IconButton>
            </Stack>
          )}
        </Box>

        {vehiclePrices.length === 0 ? (
          <Box sx={{ bgcolor: '#FEF3C7', px: 1, py: 0.5, borderRadius: 1, border: '1px solid #FCD34D' }}>
            <Typography variant="caption" fontWeight={600} color="#B45309" fontSize="0.7rem">
              Rp 6.800/L (Default)
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {paginatedPrices.map((item, idx) => {
              if (item.isDefault) {
                return (
                  <Box
                    key={`vp-${currentPage}-${idx}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#FEF3C7',
                      px: 1,
                      py: 0.4,
                      borderRadius: 1,
                      border: '1px solid #FCD34D'
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#B45309" fontSize="0.7rem" noWrap sx={{ maxWidth: 130 }}>
                      {item.licensePlate}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="#B45309" fontSize="0.7rem">
                      Rp 6.800/L (Def)
                    </Typography>
                  </Box>
                );
              }

              const priceStr = `Rp ${Number(item.price).toLocaleString('id-ID')}/L`;
              const typeStr = item.fuel_type_name || 'Solar';

              return (
                <Box
                  key={`vp-${currentPage}-${idx}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#F8FAFC',
                    px: 1,
                    py: 0.4,
                    borderRadius: 1,
                    border: '1px solid #E2E8F0'
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="text.primary" fontSize="0.7rem" noWrap sx={{ maxWidth: 140 }}>
                    {item.licensePlate} — <span style={{ color: '#475569' }}>{typeStr}</span>
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#0284C7" fontSize="0.7rem">
                    {priceStr}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

function DashboardFuelPageContent() {
  const { t } = useLanguage();
  const [engineMode, setEngineMode] = useState('ice');

  const isEV = engineMode === 'ev';
  const labelUsage = isEV ? t('dashboardFuel.energyUsage', 'Energy Usage') : t('dashboardFuel.fuelUsage', 'Fuel Usage');
  const labelChartHeader = isEV
    ? t('dashboardFuel.energyConsumptionTrend', 'Energy Consumption Trend')
    : t('dashboardFuel.fuelConsumptionTrend', 'Fuel Consumption Trend');
  const labelChartSecondary = isEV
    ? t('dashboardFuel.energyTrendDesc', 'Daily Energy Comparation L/H at Set Pit')
    : t('dashboardFuel.fuelTrendDesc', 'Daily Fuel Comparation L/H at Set Pit');
  const labelTitleData = isEV
    ? t('dashboardFuel.energyConsumptionPerUnit', 'Energy Consumption per Unit')
    : t('dashboardFuel.fuelConsumptionPerUnit', 'Fuel Consumption per Unit');
  const labelUnit = isEV ? t('dashboardFuel.kWh', 'kWh') : t('dashboardFuel.liter', 'Liter');
  const MainIcon = isEV ? ElectricBoltIcon : LocalGasStationOutlinedIcon;

  const [autoRefresh, setAutoRefresh] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchQuery, setSearchQuery] = useState('');

  const focusedSectionRef = useRef(null);

  const [fullChartData, setFullChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  const [viewAllChart, setViewAllChart] = useState(false);
  const [chartPage, setChartPage] = useState(0);
  const CHART_PAGE_SIZE = 12;
  const [periodType, setPeriodType] = useState('last_week');
  const [fromDate, setFromDate] = useState(() => dayjs().subtract(7, 'day').format('YYYY-MM-DD 00:00:00'));
  const [toDate, setToDate] = useState(() => dayjs().format('YYYY-MM-DD 23:59:59'));
  const [dateError, setDateError] = useState('');

  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    if (fromDate && toDate) {
      if (dayjs(toDate).diff(dayjs(fromDate), 'day') > 31) {
        setDateError(t('dashboardFuel.maxOneMonth', 'The selected period cannot exceed 1 month.'));
      } else {
        setDateError('');
      }
    }
  }, [fromDate, toDate, t]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      let storedCompanyId = localStorage.getItem('companyId');

      if (!storedCompanyId) {
        try {
          const rawUser = localStorage.getItem('user');
          if (rawUser) {
            const user = JSON.parse(rawUser);
            storedCompanyId = String(user.company ?? user.company_id ?? '');
          }
        } catch (err) {
          console.error('Gagal mem-parsing user dari localStorage', err);
        }
      }

      if (storedCompanyId) {
        setCompanyId(storedCompanyId);
      }
    }
  }, []);

  const { data: vehicleList } = useMasterVehicleList();

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c),
        label: c.license_plate ?? c.name ?? String(c.id ?? ''),
        company_id: String(c.company ?? c.company?.id ?? '')
      })),
    [vehicleList]
  );

  const getActiveCompanyId = useCallback(
    (vehicleIdsToCheck) => {
      if (vehicleIdsToCheck && vehicleIdsToCheck.length > 0) {
        const firstId = vehicleIdsToCheck[0];
        const match = vehicleOptionsNormalized.find((v) => v.value === String(firstId));
        if (match && match.company_id) {
          return match.company_id;
        }
      }
      return companyId;
    },
    [vehicleOptionsNormalized, companyId]
  );

  const autocompleteOptions = useMemo(() => {
    return [{ value: 'all', label: t('dashboardFuel.selectAll', 'Select All') }, ...vehicleOptionsNormalized];
  }, [vehicleOptionsNormalized, t]);

  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);

  const selectedVehicleObjects = useMemo(() => {
    return vehicleOptionsNormalized.filter((v) => selectedVehicleIds.includes(v.value));
  }, [selectedVehicleIds, vehicleOptionsNormalized]);

  const [tableData, setTableData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [focusedVehicleId, setFocusedVehicleId] = useState(null);
  const [focusedVehicleName, setFocusedVehicleName] = useState('');

  const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;

  const buildDashboardPayload = (companyId, vehicleIds = [], startIso, endIso) => {
    const columns = [];
    if (companyId) columns.push({ logic_operator: '=', name: 'company_id', operator: 'AND', value: String(companyId) });
    if (vehicleIds && vehicleIds.length) {
      columns.push({ logic_operator: 'IN', name: 'vehicle_id', operator: 'AND', value: vehicleIds.join(',') });
    }
    if (startIso && endIso) {
      columns.push({ logic_operator: 'RANGE', name: 'wp_time', operator: 'AND', value: startIso, value1: endIso });
    }

    return {
      columns,
      joins: [],
      orders: { columns: ['name'], ascending: false },
      paging: { start: 0, length: 1000 }
    };
  };

  const fetchUserVehicleAccessAndVehicles = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return;
      const user = JSON.parse(rawUser);
      const userId = user?.id;

      if (userId) {
        await fleetApi.get(`/user-vehicle-access/${userId}`);
      }
    } catch (err) {
      console.error('fetch vehicles failed', err);
    }
  }, []);

  const [summary, setSummary] = useState(null);

  const fetchAllData = useCallback(
    async (companyId, allVehicleIds, targetVehicleIds, fromDateStr, toDateStr, isFocusedMode = false) => {
      if (!fromDateStr || !toDateStr || allVehicleIds.length === 0) {
        setSummary(null);
        if (!isFocusedMode) setTableData([]);
        setFullChartData([]);
        setLoadingTable(false);
        setLoadingChart(false);
        return;
      }

      if (!isFocusedMode) {
        setLoadingTable(true);
        setTableData([]);
        setLoadingProgress(0);
        setLoadingText(t('dashboardFuel.fetchingData', 'Fetching data...'));
      }
      setLoadingChart(true);
      setChartPage(0);
      setFullChartData([]);
      setSummary(null);

      try {
        const startTimeStr = dayjs(fromDateStr).format('HH:mm:ss');
        const endTimeStr = dayjs(toDateStr).format('HH:mm:ss');

        let dates = [];
        let curr = dayjs(fromDateStr).startOf('day');
        const end = dayjs(toDateStr).startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          dates.push(curr.format('YYYY-MM-DD'));
          curr = curr.add(1, 'day');
        }

        // We need to fetch data for all vehicles in `fetchIds`
        const fetchIds = isFocusedMode ? targetVehicleIds : allVehicleIds;

        const totalVehicles = fetchIds.length;
        const totalDates = dates.length;
        const totalRequests = totalVehicles * totalDates;
        let completedRequests = 0;

        const vehicleDataMap = {};
        fetchIds.forEach((vId) => {
          vehicleDataMap[vId] = {
            actualFuel: 0,
            standardFuel: 0,
            diffFuel: 0,
            ritase: 0,
            total_mileage: 0,
            standard_cost: 0,
            actual_cost: 0,
            potential_saving: 0,
            total_seconds: 0
          };
        });

        const chartDataMap = {};
        dates.forEach((date) => {
          chartDataMap[date] = {
            date: dayjs(date).format('DD/MM'),
            fullDate: date,
            standard_fuel_consumption: 0,
            actual_fuel_consumption: 0,
            difference_fuel_consumption: 0,
            standard_cost: 0,
            actual_cost: 0,
            vehicle_fuel_prices: []
          };
        });

        // SEQUENTIAL FETCHING PER DAY
        for (const dateStr of dates) {
          let dayStartTimeStr = '00:00:00';
          let dayEndTimeStr = '23:59:59';

          const searchStartDay = dayjs(fromDateStr).format('YYYY-MM-DD');
          const searchEndDay = dayjs(toDateStr).format('YYYY-MM-DD');

          if (dateStr === searchStartDay) {
            dayStartTimeStr = startTimeStr;
          }
          if (dateStr === searchEndDay) {
            dayEndTimeStr = endTimeStr;
          }

          const startIso = formatDateMinusTz(dateStr, dayStartTimeStr, tz);
          const endIso = formatDateMinusTz(dateStr, dayEndTimeStr, tz);

          for (const vId of fetchIds) {
            const cId = getActiveCompanyId([vId]);
            const payload = buildDashboardPayload(cId, [vId], startIso, endIso);

            // Fetch Fuel
            try {
              const resp = await fleetApi.post(`/dashboard-fuel-consumption?tz=${tz}&withoutLogicAllSensor=true`, payload);
              const data = resp?.data?.data ?? resp?.data;

              if (data && Object.keys(data).length > 0) {
                vehicleDataMap[vId].actualFuel += data.actual_fuel_consumption ?? 0;
                vehicleDataMap[vId].standardFuel += data.standard_fuel_consumption ?? 0;
                vehicleDataMap[vId].diffFuel += data.difference_fuel_consumption ?? 0;
                vehicleDataMap[vId].total_mileage += data.total_mileage ?? 0;
                vehicleDataMap[vId].standard_cost += data.standard_cost ?? 0;
                vehicleDataMap[vId].actual_cost += data.actual_cost ?? 0;
                vehicleDataMap[vId].potential_saving += data.potential_saving ?? 0;

                if (data.duration) {
                  const parts = data.duration.split(':');
                  if (parts.length === 3) {
                    vehicleDataMap[vId].total_seconds +=
                      parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
                  }
                }

                // If vId is in targetVehicleIds, accumulate to chart
                if (targetVehicleIds.includes(vId)) {
                  chartDataMap[dateStr].actual_fuel_consumption += data.actual_fuel_consumption ?? 0;
                  chartDataMap[dateStr].standard_fuel_consumption += data.standard_fuel_consumption ?? 0;
                  chartDataMap[dateStr].difference_fuel_consumption += data.difference_fuel_consumption ?? 0;
                  chartDataMap[dateStr].actual_cost += data.actual_cost ?? 0;
                  chartDataMap[dateStr].standard_cost += data.standard_cost ?? 0;

                  const vehicleInfo = vehicleOptionsNormalized.find((v) => v.value === String(vId));
                  const vLabel = vehicleInfo ? vehicleInfo.label : `Vehicle ${vId}`;

                  const priceInfos = Array.isArray(data.fuel_price_info)
                    ? data.fuel_price_info
                    : data.fuel_price_info
                      ? [data.fuel_price_info]
                      : [];

                  if (priceInfos.length > 0) {
                    priceInfos.forEach((fp) => {
                      chartDataMap[dateStr].vehicle_fuel_prices.push({
                        vehicleId: vId,
                        vehicleName: fp.vehicle_name || fp.license_plate || vLabel,
                        licensePlate: fp.license_plate || fp.vehicle_name || vLabel,
                        fuel_type_name: fp.fuel_type_name || '',
                        fuel_type_code: fp.fuel_type_code || '',
                        price: fp.price ?? 6800,
                        valid_at: fp.valid_at || null,
                        isDefault: false
                      });
                    });
                  } else {
                    chartDataMap[dateStr].vehicle_fuel_prices.push({
                      vehicleId: vId,
                      vehicleName: vLabel,
                      licensePlate: vLabel,
                      fuel_type_name: '',
                      fuel_type_code: '',
                      price: 6800,
                      valid_at: null,
                      isDefault: true
                    });
                  }
                }
              }
            } catch (err) {
              console.error(err);
            }

            // Fetch Ritase (hanya jika tidak dalam mode focus detail/chart)
            if (!isFocusedMode) {
              const finalStartIso = dayjs(`${dateStr} ${dayStartTimeStr}`).subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:00[Z]');
              const finalEndIso = dayjs(`${dateStr} ${dayEndTimeStr}`).subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:59[Z]');

              try {
                const retaseParams = {
                  vehicleId: vId,
                  companyId: String(cId),
                  tz,
                  startTime: finalStartIso,
                  endTime: finalEndIso
                };
                const retaseResp = await fetchRetase(retaseParams);
                const retaseData = retaseResp?.data?.retase ?? [];

                const uniqueTripsMap = new Map();
                retaseData.forEach((tripData) => {
                  const routes = tripData.routes || [];
                  const details = tripData.details || [];
                  const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                  const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;
                  let earliestStart = null;
                  let latestEnd = null;
                  details.forEach((d) => {
                    const st = dayjs(d.start);
                    const en = dayjs(d.end);
                    if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                    if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                  });
                  const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                  const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';
                  const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                  if (!uniqueTripsMap.has(uniqueKey)) {
                    uniqueTripsMap.set(uniqueKey, tripData);
                  }
                });

                const apiDataRaw = Array.from(uniqueTripsMap.values());
                apiDataRaw.forEach((trip) => {
                  if (trip.details && Array.isArray(trip.details)) {
                    if (trip.details.length === 0) {
                      vehicleDataMap[vId].ritase += trip.total_trips || 1;
                    } else {
                      vehicleDataMap[vId].ritase += trip.details.length;
                    }
                  } else if (!trip.details) {
                    vehicleDataMap[vId].ritase += trip.total_trips || 1;
                  }
                });
              } catch (err) {
                console.error(err);
              }
            }

            completedRequests++;
            if (!isFocusedMode) {
              setLoadingProgress(Math.round((completedRequests / totalRequests) * 100));
              setLoadingText(t('dashboardFuel.fetchingData', 'Fetching data...'));
            }
          }
        }

        let sumTotalMileage = 0;
        let sumStandardFuel = 0;
        let sumActualFuel = 0;
        let sumDiffFuel = 0;
        let sumStandardCost = 0;
        let sumActualCost = 0;
        let sumPotentialSaving = 0;
        let sumTotalSeconds = 0;

        if (!isFocusedMode) {
          const newTableData = [];
          allVehicleIds.forEach((vId) => {
            const vData = vehicleDataMap[vId];
            if (vData && (vData.actualFuel > 0 || vData.standardFuel > 0 || vData.ritase > 0)) {
              const vehicleInfo = vehicleOptionsNormalized.find((v) => v.value === String(vId));
              newTableData.push({
                id: vId,
                vehicleNumber: vehicleInfo ? vehicleInfo.label : vId,
                actualFuel: vData.actualFuel,
                standardFuel: vData.standardFuel,
                diffFuel: vData.diffFuel,
                ritase: vData.ritase
              });
            }
          });
          setTableData(newTableData);
        }

        targetVehicleIds.forEach((vId) => {
          const vData = vehicleDataMap[vId];
          if (vData) {
            sumTotalMileage += vData.total_mileage;
            sumStandardFuel += vData.standardFuel;
            sumActualFuel += vData.actualFuel;
            sumDiffFuel += vData.diffFuel;
            sumStandardCost += vData.standard_cost;
            sumActualCost += vData.actual_cost;
            sumPotentialSaving += vData.potential_saving;
            sumTotalSeconds += vData.total_seconds;
          }
        });

        const newChartData = dates.map((date) => chartDataMap[date]);
        setFullChartData(newChartData);

        const hrs = Math.floor(sumTotalSeconds / 3600);
        const mins = Math.floor((sumTotalSeconds % 3600) / 60);
        const secs = sumTotalSeconds % 60;
        const durationStr = `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

        setSummary({
          total_mileage: sumTotalMileage,
          duration: durationStr,
          standard_fuel: sumStandardFuel,
          standard_cost: sumStandardCost,
          actual_fuel: sumActualFuel,
          actual_cost: sumActualCost,
          difference_fuel: sumDiffFuel,
          potential_saving: sumPotentialSaving,
          status:
            sumDiffFuel > 0 ? t('dashboardFuel.wasteful', 'Wasteful') : sumDiffFuel < 0 ? t('dashboardFuel.efficient', 'Efficient') : '-'
        });
      } catch (err) {
        console.error('Fetch all data failed', err);
      } finally {
        if (!isFocusedMode) setLoadingTable(false);
        setLoadingChart(false);
      }
    },
    [t, getActiveCompanyId, tz, vehicleOptionsNormalized]
  );

  useEffect(() => {
    if (!companyId) return;
    fetchUserVehicleAccessAndVehicles(companyId);
  }, [companyId, fetchUserVehicleAccessAndVehicles]);

  // Tambahkan efek ini untuk me-render ulang nama vehicle ketika data vehicleList sudah siap (setelah initial load)
  useEffect(() => {
    if (vehicleOptionsNormalized.length > 0 && tableData.length > 0) {
      setTableData((prev) =>
        prev.map((row) => {
          const match = vehicleOptionsNormalized.find((v) => v.value === String(row.id));
          if (match && String(row.vehicleNumber) === String(row.id)) {
            return { ...row, vehicleNumber: match.label };
          }
          return row;
        })
      );
    }
  }, [tableData.length, vehicleOptionsNormalized]);

  useEffect(() => {
    if (autoRefresh <= 0 || periodType !== 'today') return;

    const intervalMs = autoRefresh * 60 * 1000;
    const timer = setInterval(() => {
      if (!companyId || selectedVehicleIds.length === 0) return;

      const newFrom = dayjs().format('YYYY-MM-DD 00:00:00');
      const newTo = dayjs().format('YYYY-MM-DD HH:mm:59');

      setFromDate(newFrom);
      setToDate(newTo);

      const targetIds = focusedVehicleId ? [focusedVehicleId] : selectedVehicleIds;
      const activeCid = getActiveCompanyId(targetIds);

      fetchAllData(activeCid, selectedVehicleIds, targetIds, newFrom, newTo, false);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [autoRefresh, periodType, companyId, selectedVehicleIds, focusedVehicleId, fetchAllData, getActiveCompanyId]);

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setPeriodType(val);
    if (val !== 'today') {
      setAutoRefresh(0);
    }

    if (val === 'today') {
      setFromDate(dayjs().format('YYYY-MM-DD 00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DD HH:mm:59'));
    } else if (val === 'last_week') {
      setFromDate(dayjs().subtract(7, 'day').format('YYYY-MM-DD 00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DD 23:59:59'));
    } else if (val === 'last_month') {
      setFromDate(dayjs().subtract(1, 'month').format('YYYY-MM-DD 00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DD 23:59:59'));
    } else {
      // default custom
      setFromDate(dayjs().startOf('day').format('YYYY-MM-DD 00:00:00'));
      setToDate(dayjs().endOf('day').format('YYYY-MM-DD 23:59:59'));
    }
  };

  const handleManualRefresh = () => {
    if (!companyId || !fromDate || !toDate || selectedVehicleIds.length === 0) return;
    if (dayjs(toDate).diff(dayjs(fromDate), 'day') > 31) {
      setDateError(t('dashboardFuel.maxOneMonth', 'The selected period cannot exceed 1 month.'));
      return;
    }
    setDateError('');
    const targetIds = focusedVehicleId ? [focusedVehicleId] : selectedVehicleIds;
    const activeCid = getActiveCompanyId(targetIds);
    fetchAllData(activeCid, selectedVehicleIds, targetIds, fromDate, toDate, false);
  };

  const handleViewDaily = useCallback(
    (row) => {
      setFocusedVehicleId(row.id);
      setFocusedVehicleName(row.vehicleNumber);
      const activeCid = getActiveCompanyId([row.id]);
      fetchAllData(activeCid, selectedVehicleIds, [row.id], fromDate, toDate, true);

      setTimeout(() => {
        if (focusedSectionRef.current) {
          focusedSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    },
    [fromDate, toDate, fetchAllData, getActiveCompanyId, selectedVehicleIds]
  );

  useEffect(() => {
    if (focusedVehicleId && focusedSectionRef.current) {
      setTimeout(() => {
        focusedSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [focusedVehicleId]);

  const paginatedChartData = useMemo(() => {
    if (viewAllChart) return fullChartData;
    const startIndex = chartPage * CHART_PAGE_SIZE;
    const endIndex = startIndex + CHART_PAGE_SIZE;
    return fullChartData.slice(startIndex, endIndex);
  }, [fullChartData, chartPage, viewAllChart]);

  const displayChartData = useMemo(() => {
    if (paginatedChartData.length === 1) {
      return [
        { ...paginatedChartData[0], date: ` ${paginatedChartData[0].date}` },
        { ...paginatedChartData[0], date: `${paginatedChartData[0].date} ` }
      ];
    }
    return paginatedChartData;
  }, [paginatedChartData]);

  const totalPages = Math.ceil(fullChartData.length / CHART_PAGE_SIZE);

  const handlePrevPage = () => setChartPage((prev) => Math.max(prev - 1, 0));
  const handleNextPage = () => setChartPage((prev) => Math.min(prev + 1, totalPages - 1));

  const summarySafe = summary ?? {
    total_mileage: 0,
    duration: '00:00:00',
    standard_fuel: 0,
    standard_cost: 0,
    actual_fuel: 0,
    actual_cost: 0,
    difference_fuel: 0,
    potential_saving: 0,
    status: '—'
  };

  const filteredTableData = useMemo(() => {
    if (!searchQuery) return tableData;
    const lowerQ = searchQuery.toLowerCase();
    return tableData.filter((row) => {
      return String(row.vehicleNumber || '')
        .toLowerCase()
        .includes(lowerQ);
    });
  }, [tableData, searchQuery]);

  const tableColumns = useMemo(
    () => [
      { field: 'vehicleNumber', headerName: t('dashboardFuel.vehicleNumber', 'Vehicle Number'), flex: 1, minWidth: 150 },
      {
        field: 'actualFuel',
        headerName: isEV ? t('dashboardFuel.actualEnergy', 'Actual Energy (kWh)') : t('dashboardFuel.actualFuel', 'Actual Fuel (L)'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        type: 'number'
      },
      {
        field: 'standardFuel',
        headerName: isEV
          ? t('dashboardFuel.standardEnergy', 'Standard Energy (kWh)')
          : t('dashboardFuel.standardFuel', 'Standard Fuel (L)'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        type: 'number'
      },
      {
        field: 'diffFuel',
        headerName: t('dashboardFuel.diffFuel', 'Difference (Standard - Actual)'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        type: 'number'
      },
      {
        field: 'ritase',
        headerName: t('dashboardFuel.ritase', 'Ritase'),
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        type: 'number'
      },
      {
        field: 'action',
        headerName: t('dashboardFuel.action', 'Action'),
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button variant="text" size="small" onClick={() => handleViewDaily(params.row)} startIcon={<VisibilityIcon />}>
            {t('dashboardFuel.viewDaily', 'View Daily')}
          </Button>
        )
      }
    ],
    [t, isEV, handleViewDaily]
  );

  // --- Helper conditional rendering widgets ---
  const appSettingStr = localStorage.getItem('appSetting');
  const appSetting = appSettingStr ? JSON.parse(appSettingStr) : {};
  const widgetsConfig = appSetting?.widgets || null;
  const showWidget = (key) => {
    if (!widgetsConfig) return true;
    if (!widgetsConfig.expense) return true;
    return widgetsConfig.expense[key] !== false;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: '100vh', mt: 2 }}>
        {/* === BAGIAN FILTER === */}
        <Box sx={{ mb: 1, p: 2, bgcolor: '#ffffff', borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={2} alignItems="center">
            {/* BARIS 1: Engine Mode & Auto Refresh */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <ToggleButtonGroup
                color="primary"
                value={engineMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) setEngineMode(newMode);
                }}
                size="medium"
                sx={{ bgcolor: '#fff', height: 41 }}
              >
                <ToggleButton value="ice" sx={{ px: 3, fontWeight: 'bold' }}>
                  {t('dashboardFuel.bbmConventional', 'Conventional Vehicle')}
                </ToggleButton>
                <ToggleButton value="ev" sx={{ px: 3, fontWeight: 'bold' }}>
                  <ElectricBoltIcon fontSize="small" sx={{ mr: 1 }} /> {t('dashboardFuel.evDummy', 'Electric Vehicle')} (Dummy)
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1}>
              <TextField
                select
                size="medium"
                sx={{ minWidth: 150, height: 41 }}
                label={t('dashboardFuel.refreshEvery', 'Refresh Every')}
                value={autoRefresh}
                onChange={(e) => setAutoRefresh(Number(e.target.value))}
                disabled={periodType !== 'today'}
              >
                <MenuItem value={0}>{t('dashboardFuel.off', 'Off')}</MenuItem>
                <MenuItem value={10}>{t('dashboardFuel.minutes10', '10 Minutes')}</MenuItem>
                <MenuItem value={30}>{t('dashboardFuel.minutes30', '30 Minutes')}</MenuItem>
                <MenuItem value={60}>{t('dashboardFuel.minutes60', '60 Minutes')}</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                onClick={handleManualRefresh}
                disabled={selectedVehicleIds.length === 0}
                sx={{
                  width: 41,
                  height: 41,
                  minWidth: 41,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0,
                  borderColor: '#485D9A',
                  '&:hover': { borderColor: '#485D9A' }
                }}
              >
                <HistoryIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
              </Button>
            </Grid>

            {/* BARIS 2: Filters */}
            <Grid item size={{ xs: 12, md: 3 }}>
              <Autocomplete
                multiple
                label={t('dashboardFuel.vehicle', 'Vehicle')}
                disableCloseOnSelect
                options={autocompleteOptions}
                getOptionLabel={(option) => option.label || ''}
                value={selectedVehicleObjects}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(event, newValue) => {
                  if (newValue.find((v) => v.value === 'all')) {
                    if (selectedVehicleIds.length === vehicleOptionsNormalized.length) {
                      setSelectedVehicleIds([]);
                    } else {
                      setSelectedVehicleIds(vehicleOptionsNormalized.map((v) => v.value));
                    }
                    return;
                  }
                  const ids = newValue.map((v) => v.value);
                  setSelectedVehicleIds(ids);
                }}
                renderTags={(tagValue, getTagProps) => {
                  const numTags = tagValue.length;
                  const totalOptions = vehicleOptionsNormalized.length;
                  if (numTags === 0) return null;
                  if (numTags === totalOptions && totalOptions > 0) {
                    return (
                      <Chip
                        label={t('dashboardFuel.allVehicles', 'All Vehicles')}
                        size="small"
                        sx={{ bgcolor: '#485D9A', color: 'white', fontWeight: 'bold', ml: 1, height: 24 }}
                      />
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', alignItems: 'center' }}>
                      {tagValue.slice(0, 2).map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                          sx={{
                            height: 24,
                            bgcolor: '#EDF3FC',
                            color: '#485D9A',
                            fontWeight: 600,
                            border: '1px solid #9CCBF9'
                          }}
                        />
                      ))}
                      {numTags > 2 && (
                        <Chip
                          label={`+${numTags - 2}`}
                          size="small"
                          sx={{ height: 24, bgcolor: '#485D9A', color: '#fff', fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  const isAllOption = option.value === 'all';
                  const isAllSelected =
                    vehicleOptionsNormalized.length > 0 && selectedVehicleIds.length === vehicleOptionsNormalized.length;
                  const isChecked = isAllOption ? isAllSelected : selected;
                  return (
                    <li {...props} key={option.value}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8, padding: 4 }}
                        checked={isChecked}
                        color="primary"
                      />
                      <Typography sx={{ fontWeight: isAllOption ? 700 : 500, fontSize: '0.875rem' }}>{option.label}</Typography>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selectedVehicleIds.length === 0 ? t('dashboardFuel.searchPlateNumber', 'Search plate number...') : ''}
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        minHeight: 38,
                        p: '4px 8px',
                        borderRadius: 1,
                        '& fieldset': { borderColor: '#485D9A' },
                        '&:hover fieldset': { borderColor: '#485D9A' },
                        '&.Mui-focused fieldset': { borderColor: '#485D9A', borderWidth: 2 }
                      },
                      '& .MuiInputLabel-root': {
                        transform: 'translate(14px, 10px) scale(1)',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                          color: '#485D9A',
                          fontWeight: 'bold'
                        }
                      }
                    }}
                  />
                )}
                sx={{
                  width: '100%',
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'nowrap !important',
                    overflow: 'hidden'
                  }
                }}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('dashboardFuel.period', 'Period')}
                value={periodType}
                onChange={handlePeriodChange}
                sx={{ ...selectFieldStyle, '& .MuiInputBase-root': { height: 38 } }}
              >
                <MenuItem value="today">{t('dashboardFuel.today', 'Today')}</MenuItem>
                <MenuItem value="last_week">{t('time.lastWeek', 'Last week')}</MenuItem>
                <MenuItem value="last_month">{t('time.lastMonth', 'Last month')}</MenuItem>
                <MenuItem value="custom">{t('time.custom', 'Custom')}</MenuItem>
              </TextField>
            </Grid>
            {periodType === 'custom' ? (
              <Grid container item size={{ xs: 12, md: 6 }} spacing={2}>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    timeSteps={{ minutes: 1 }}
                    ampm={false}
                    label={t('dashboardFuel.fromDate', 'From Date')}
                    format="DD/MM/YYYY HH:mm"
                    value={fromDate ? dayjs(fromDate) : null}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    onChange={(newValue) => setFromDate(newValue ? newValue.format('YYYY-MM-DD HH:mm:00') : '')}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-root': { height: 38 } } } }}
                  />
                  {dateError && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 500, lineHeight: 1.2 }}>
                      {dateError}
                    </Typography>
                  )}
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    timeSteps={{ minutes: 1 }}
                    ampm={false}
                    label={t('dashboardFuel.toDate', 'To Date')}
                    format="DD/MM/YYYY HH:mm"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(newValue) => setToDate(newValue ? newValue.format('YYYY-MM-DD HH:mm:59') : '')}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-root': { height: 38 } } } }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid item size={{ xs: 12, md: 6 }} display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#F3F4F6', px: 2, py: 1, borderRadius: 1, width: '100%' }}
                >
                  {t('dashboardFuel.selectedPeriod', 'Selected Period:')} {dayjs(fromDate).format('DD MMM YYYY HH:mm')} -{' '}
                  {dayjs(toDate).format('DD MMM YYYY HH:mm')}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="contained" onClick={handleManualRefresh} sx={{ height: 38, fontWeight: 'bold', fontSize: 14 }}>
                {t('dashboardFuel.search', 'Search')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/*TABEL FUEL CONSUMPTION PER UNIT*/}
        {showWidget('fuel-consumption-per-unit') && (
          <Card sx={{ p: 2, borderRadius: 2, boxShadow: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    bgcolor: '#485D9A',
                    color: 'white',
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <VerifiedUserIcon />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {labelTitleData}
                </Typography>
              </Stack>
            </Stack>

            {selectedVehicleIds.length === 0 ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body1" color="text.secondary" fontWeight="medium">
                  {t('dashboardFuel.pleaseSelectVehicle', 'Please select a vehicle in the search box above to display data.')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ '& > .MuiPaper-root': { minHeight: 400, boxShadow: 'none', border: 'none', p: 0 } }}>
                {loadingTable && loadingProgress > 0 ? (
                  <Box sx={{ width: '60%', mx: 'auto', mt: 10, mb: 10, textAlign: 'center' }}>
                    <LinearProgress
                      variant="determinate"
                      value={loadingProgress}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        mb: 1.5,
                        bgcolor: 'rgba(72, 93, 154, 0.2)',
                        '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" fontWeight="bold">
                      {loadingProgress}% - {loadingText}
                    </Typography>
                  </Box>
                ) : (
                  <DataTableV2
                    rows={filteredTableData}
                    columns={tableColumns}
                    loading={loadingTable}
                    hideExportButton={true}
                    searchFieldOptions={[]}
                    searchQuery={searchQuery}
                    onSearchChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    searchLabel="Search by vehicle"
                    pagination
                    paginationMode="client"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 50, 100]}
                  />
                )}
              </Box>
            )}
          </Card>
        )}

        <Box ref={focusedSectionRef} sx={{ mb: 2 }}>
          {focusedVehicleId && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                boxShadow: 3
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(72, 93, 154, 0.3)'
                  }}
                >
                  <DirectionsCarIcon fontSize="medium" />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="primary.main"
                    fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}
                  >
                    {t('dashboardFuel.vehicleFocus', 'Vehicle Focus')}
                  </Typography>
                  <Typography variant="h4" color="text.primary" fontWeight="bold">
                    {focusedVehicleName}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                color="primary"
                startIcon={<DashboardIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  px: 3,
                  py: 1,
                  '&:hover': { boxShadow: 'none', bgcolor: 'primary.dark' }
                }}
                onClick={() => {
                  setFocusedVehicleId(null);
                  setFocusedVehicleName('');
                  const activeCid = getActiveCompanyId(selectedVehicleIds);
                  fetchAllData(activeCid, selectedVehicleIds, selectedVehicleIds, fromDate, toDate, true);
                }}
              >
                {t('dashboardFuel.viewAccumulationAllUnits', 'View Accumulation of All Units')}
              </Button>
            </Paper>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            gap: 2,
            mb: 2,
            alignItems: 'stretch'
          }}
        >
          {/* CARD 1: Trip Summary */}
          {showWidget('summary-fuel') && (
            <Card
              sx={{
                background: '#ffffff',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(72,93,154,0.08)',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                '&:hover': { boxShadow: '0 6px 24px rgba(72,93,154,0.15)', transform: 'translateY(-2px)' },
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, letterSpacing: 0.3 }}>
                  {t('dashboardFuel.tripSummary', 'Trip Summary')}
                </Typography>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ flex: 1, bgcolor: '#EDF3FC', borderRadius: 2.5, display: 'flex', alignItems: 'center', p: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: 'rgba(72,93,154,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0
                      }}
                    >
                      <DriveEtaIcon sx={{ fontSize: 26, color: '#485D9A' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {t('dashboardFuel.totalMileage', 'Total Mileage')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        {(summarySafe.total_mileage || 0).toLocaleString()} km
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: '#EDF3FC', borderRadius: 2.5, display: 'flex', alignItems: 'center', p: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: 'rgba(72,93,154,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 26, color: '#485D9A' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {t('dashboardFuel.totalDurations', 'Total Durations')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        {summarySafe.duration}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* CARD 2: Fuel Usage */}
          {showWidget('summary-fuel') && (
            <Card
              sx={{
                background: '#ffffff',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(72,93,154,0.08)',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                '&:hover': { boxShadow: '0 6px 24px rgba(72,93,154,0.15)', transform: 'translateY(-2px)' },
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, letterSpacing: 0.3 }}>
                  {labelUsage} {/* <-- Dinamis */}
                </Typography>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Standard Box */}
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: '#FFDDAE',
                      border: '1.5px solid #FCB859',
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: 'rgba(72,93,154,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0
                      }}
                    >
                      <MainIcon sx={{ fontSize: 26, color: '#485D9A' }} /> {/* <-- Dinamis */}
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ opacity: 0.75 }}>
                        {t('dashboardFuel.standard', 'Standard')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        {(summarySafe.standard_fuel || 0).toLocaleString()} {labelUnit} {/* <-- Dinamis */}
                      </Typography>
                      <Divider sx={{ borderColor: 'rgba(0,0,0,0.15)', my: 0.5 }} />
                      <Typography variant="body1" fontWeight={600} sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        Rp {(summarySafe.standard_cost || 0).toLocaleString('id-ID')}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Actual Box */}
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: '#C4FFCA',
                      border: '1.5px solid #01FF05',
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: 'rgba(72,93,154,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0
                      }}
                    >
                      <MainIcon sx={{ fontSize: 26, color: '#485D9A' }} /> {/* <-- Dinamis */}
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ opacity: 0.75 }}>
                        {t('dashboardFuel.actual', 'Actual')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        {(summarySafe.actual_fuel || 0).toLocaleString()} {labelUnit} {/* <-- Dinamis */}
                      </Typography>
                      <Divider sx={{ borderColor: 'rgba(0,0,0,0.15)', my: 0.5 }} />
                      <Typography variant="body1" fontWeight={600} sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        Rp {(summarySafe.actual_cost || 0).toLocaleString('id-ID')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* CARD 3: Difference */}
          {showWidget('summary-fuel') && (
            <Card
              sx={{
                background: '#ffffff',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(72,93,154,0.08)',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                '&:hover': { boxShadow: '0 6px 24px rgba(72,93,154,0.15)', transform: 'translateY(-2px)' },
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, letterSpacing: 0.3 }}>
                  {t('dashboardFuel.difference', 'Difference')}
                </Typography>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: '#EDF3FC',
                      borderRadius: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      p: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {t('dashboardFuel.standard', 'Standard')} − {t('dashboardFuel.actual', 'Actual')}:
                      </Typography>
                      <Chip
                        label={summarySafe.status}
                        size="small"
                        sx={{
                          color: '#fff',
                          bgcolor: summarySafe.status === 'normal' ? '#01FF05' : 'orange',
                          textTransform: 'capitalize',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 24
                        }}
                      />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                      {(summarySafe.difference_fuel || 0).toLocaleString()} {labelUnit} {/* <-- Dinamis */}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: '#EDF3FC',
                      borderRadius: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      p: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {t('dashboardFuel.potentialSavings', 'Potential Savings')}:
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mt: 0.5,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        color: summarySafe.potential_saving < 0 ? '#D32F2F' : '#1B5E20'
                      }}
                    >
                      Rp {(summarySafe.potential_saving || 0).toLocaleString('id-ID')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* DASHBOARD CHART */}
        {showWidget('fuel-consumption-trend') && (
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent sx={{ height: 500, p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" justifyContent="start" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#485D9A',
                      color: '#ffffff',
                      borderRadius: 2,
                      width: 40,
                      height: 40
                    }}
                  >
                    <InsertChartOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {labelChartHeader}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#353535' }}>
                      {labelChartSecondary}
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" alignItems="center" spacing={2}>
                  {fullChartData.length > CHART_PAGE_SIZE && (
                    <FormControlLabel
                      control={
                        <Switch size="small" checked={viewAllChart} onChange={(e) => setViewAllChart(e.target.checked)} color="primary" />
                      }
                      label={<Typography variant="body2">{t('dashboardFuel.viewAll', 'View All')}</Typography>}
                      sx={{ m: 0 }}
                    />
                  )}
                  {fullChartData.length > CHART_PAGE_SIZE && !viewAllChart && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton onClick={handlePrevPage} disabled={chartPage === 0} size="small" sx={{ border: '1px solid #ccc' }}>
                        <ArrowBackIosIcon fontSize="inherit" />
                      </IconButton>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {paginatedChartData.length > 0
                          ? `${paginatedChartData[0].date} - ${paginatedChartData[paginatedChartData.length - 1].date}`
                          : ''}
                      </Typography>
                      <IconButton
                        onClick={handleNextPage}
                        disabled={chartPage >= totalPages - 1}
                        size="small"
                        sx={{ border: '1px solid #ccc' }}
                      >
                        <ArrowForwardIosIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {loadingChart ? (
                <Typography align="center" mt={10}>
                  {t('dashboardFuel.loadingChart', 'Loading Chart...')}
                </Typography>
              ) : paginatedChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="90%">
                  <ComposedChart
                    data={displayChartData}
                    margin={{ top: 25, right: 30, left: 10, bottom: displayChartData.length > 12 ? 35 : 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      interval={0}
                      angle={displayChartData.length > 12 ? -45 : 0}
                      textAnchor={displayChartData.length > 12 ? 'end' : 'middle'}
                      height={displayChartData.length > 12 ? 60 : 30}
                      dy={displayChartData.length > 12 ? 10 : 0}
                    />
                    <YAxis
                      yAxisId="left"
                      orientation="left"
                      stroke="#485D9A"
                      tickFormatter={(val) => {
                        if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
                        return val;
                      }}
                    />
                    <Tooltip wrapperStyle={{ pointerEvents: 'auto' }} content={<CustomChartTooltip labelUnit={labelUnit} t={t} />} />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="actual_fuel_consumption"
                      name={t('dashboardFuel.actualFuel', 'Actual Fuel')}
                      stroke="#10B981"
                      strokeWidth={2}
                      dot={<CustomFuelPriceDot chartData={displayChartData} />}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="standard_fuel_consumption"
                      name={t('dashboardFuel.standardFuel', 'Standard Fuel')}
                      stroke="#3B82F6"
                      fill="#FF8904"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Typography align="center" mt={10}>
                  {t('dashboardFuel.noDataChart', 'No Data Available (Select Vehicle & Date Range)')}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default function DashboardFuelPage() {
  return (
    <>
      <DashboardFuelPageContent />
    </>
  );
}
